import { useNoshStore } from '../../lib/store';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, Plus, History, Clock, Trash2, Calendar, X } from 'lucide-react';
import { formatDate, daysUntilExpiry, getExpiryColor, getCategoryColor } from '../../lib/utils';
import { useEffect, useState } from 'react';
import { db } from '../../lib/database';
import type { FoodItem } from '../../lib/store';
import { Button } from '../ui/button';
import ReactDOM from 'react-dom';

// Add the location border mapping (same as inventory)
const LOCATION_BORDER_MAP: Record<string, string> = {
  fridge: 'border-l-4 border-blue-400',
  pantry: 'border-l-4 border-yellow-500',
  freezer: 'border-l-4 border-cyan-400',
  default: 'border-l-4 border-gray-300',
};

// Get background color based on expiration date (updated for freezer)
const getExpiryBackgroundColor = (days: number, location: string): string => {
  // Special handling for freezer items - they get a light blue/cyan background
  if (location === 'freezer') {
    return 'bg-cyan-50 dark:bg-cyan-900/10';
  }
  
  if (days < 0) {
    // Expired - Red background
    return 'bg-red-100 dark:bg-red-900/20';
  } else if (days === 0) {
    // Expiring today - Red background
    return 'bg-red-100 dark:bg-red-900/20';
  } else if (days === 1) {
    // 1 day - Subtle orange (toned down)
    return 'bg-orange-100 dark:bg-orange-900/15';
  } else if (days === 2) {
    // 2 days - Lighter orange (same base as 1 day, but lighter)
    return 'bg-orange-50 dark:bg-orange-900/7';
  } else if (days === 3) {
    // 3 days - Very light orange (same base as 1 day, but very light)
    return 'bg-orange-50 dark:bg-orange-900/4';
  } else if (days === 4) {
    // 4 days - Light yellow
    return 'bg-yellow-50 dark:bg-yellow-900/10';
  } else if (days === 5) {
    // 5 days - Very light yellow-green
    return 'bg-lime-50 dark:bg-lime-900/10';
  } else if (days === 6) {
    // 6 days - Light green
    return 'bg-green-50 dark:bg-green-900/10';
  } else {
    // 7+ days - Same fresh green for all
    return 'bg-green-50 dark:bg-green-900/10';
  }
};

// Helper function to get expiry color classes that handles freezer location
const getExpiryColorWithLocation = (days: number, location: string): string => {
  if (location === 'freezer') {
    return 'bg-cyan-100 text-cyan-700 dark:text-cyan-300 border border-cyan-200';
  }
  
  if (days < 0) return 'bg-destructive/20 text-destructive border border-destructive/30';
  if (days < 3) return 'bg-orange-500/15 text-orange-600 dark:text-orange-400 border border-orange-500/20';
  if (days < 7) return 'bg-amber-500/15 text-amber-600 dark:text-amber-400 border border-amber-500/20';
  return 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20';
};

// Helper function to get expiry display text
const getExpiryDisplayText = (days: number, location: string): string => {
  if (location === 'freezer') {
    return '❄️ Frozen';
  }
  
  if (days < 0) return 'Expired';
  if (days === 0) return 'Today';
  return `${days} days`;
};

// Confirmation Modal Component
const DeleteConfirmModal = ({ 
  isOpen, 
  onConfirm, 
  onCancel, 
  itemName 
}: { 
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  itemName: string;
}) => {
  if (!isOpen) return null;

  const modalContent = (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
        onClick={onCancel}
        style={{ margin: 0, padding: '1rem', border: 'none' }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-sm mx-auto overflow-hidden border-0"
          onClick={(e) => e.stopPropagation()}
          style={{ border: 'none', margin: 0 }}
        >
          {/* Header */}
          <div className="px-6 pt-6 pb-2">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Delete Item
              </h3>
              <button
                onClick={onCancel}
                className="h-8 w-8 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 flex items-center justify-center transition-all duration-200"
              >
                <X size={14} />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 pb-6">
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Are you sure you want to delete <span className="font-medium text-gray-900 dark:text-gray-100">{itemName}</span>? This action cannot be undone.
            </p>

            {/* Buttons */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={onCancel}
                className="flex-1 h-10 rounded-xl"
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={onConfirm}
                className="flex-1 h-10 rounded-xl"
              >
                Delete
              </Button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

// Expiring Soon Card Component (matching inventory design)
const ExpiringItemCard = ({ item }: { item: FoodItem }) => {
  const [isFlipped, setIsFlipped] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const { removeItem } = useNoshStore();
  const days = daysUntilExpiry(item.expiryDate);
  const cardBg = getExpiryBackgroundColor(days, item.location);
  const borderColor = LOCATION_BORDER_MAP[item.location] || LOCATION_BORDER_MAP.default;

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    await removeItem(item.id);
    setShowDeleteModal(false);
    // Refresh the page or update the expiring items list
    window.location.reload();
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
  };

  const handleAddToMealPlan = (e: React.MouseEvent) => {
    e.stopPropagation();
    // TODO: Implement add to meal planning functionality
    console.log('Add to meal plan:', item.name);
    alert('Add to meal plan feature coming soon!');
  };

  return (
    <>
      <motion.div
        className="relative w-full h-[80px] cursor-pointer perspective-1000"
        onClick={() => setIsFlipped(!isFlipped)}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <motion.div
          className="relative w-full h-full preserve-3d"
          animate={{ rotateY: isFlipped ? 180 : 0 }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
          style={{ transformStyle: "preserve-3d" }}
        >
          {/* Front of card */}
          <div
            className={`absolute inset-0 ${cardBg} ${borderColor} rounded-xl overflow-hidden shadow-soft hover:shadow-lg transition-all duration-300 flex items-center px-3 py-2 backface-hidden`}
            style={{ backfaceVisibility: "hidden" }}
          >
            {/* Emoji icon on the left */}
            <span className="text-2xl w-8 flex-shrink-0 text-center">{item.emoji}</span>
            {/* Content */}
            <div className="flex-1 flex flex-col items-start justify-center min-w-0 ml-2">
              <h3 className="font-semibold text-sm text-gray-900 mb-0.5 truncate w-full">{item.name}</h3>
              <div className="text-xs text-gray-600 font-light truncate w-full">{item.quantity} {item.unit}</div>
            </div>
            {/* Expiry tag on the right */}
            <div className={`rounded-full px-2 py-1 text-xs font-medium ${getExpiryColorWithLocation(days, item.location)} flex-shrink-0`}> 
              {getExpiryDisplayText(days, item.location)}
            </div>
          </div>

          {/* Back of card - Simplified with only buttons */}
          <div
            className={`absolute inset-0 ${cardBg} ${borderColor} rounded-xl overflow-hidden shadow-soft hover:shadow-lg transition-all duration-300 flex items-center justify-center gap-2 px-3 py-2 backface-hidden`}
            style={{ backfaceVisibility: "hidden", transform: "rotateY(180deg)" }}
          >
            <Button
              size="sm"
              variant="outline"
              className="flex-1 h-8 text-xs bg-white/90 hover:bg-white border-gray-300 font-medium"
              onClick={handleAddToMealPlan}
            >
              <Plus size={14} className="mr-1" />
              Plan
            </Button>
            <Button
              size="sm"
              variant="destructive"
              className="flex-1 h-8 text-xs font-medium"
              onClick={handleDelete}
            >
              <Trash2 size={14} className="mr-1" />
              Delete
            </Button>
          </div>
        </motion.div>
      </motion.div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        itemName={item.name}
      />
    </>
  );
};

const RecentActivity = () => {
  const { recipes } = useNoshStore();
  const [expiringItems, setExpiringItems] = useState<FoodItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Load expiring items from database
  useEffect(() => {
    const loadExpiringItems = async () => {
      try {
        setIsLoading(true);
        const items = await db.getExpiringItems();
        // Sort by expiry date (soonest first)
        const sortedItems = items.sort((a, b) => {
          const daysA = daysUntilExpiry(a.expiryDate);
          const daysB = daysUntilExpiry(b.expiryDate);
          return daysA - daysB;
        });
        setExpiringItems(sortedItems.slice(0, 5)); // Show top 5 expiring items
      } catch (error) {
        console.error('Error loading expiring items:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadExpiringItems();
    
    // Refresh every 5 minutes
    const interval = setInterval(loadExpiringItems, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);
  
  // Get last 5 recipes
  const recentRecipes = recipes.slice(0, 5);

  return (
    <motion.div 
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      className="w-64 fixed right-0 top-0 h-screen bg-card border-l border-border p-6 overflow-y-auto md:rounded-3xl md:shadow-lg md:m-4 md:h-[calc(100vh-2rem)]"
    >
      <div className="space-y-6">
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading font-semibold text-foreground">Expiring Soon</h2>
            <AlertTriangle size={16} className="text-orange-500" />
          </div>
          
          {isLoading ? (
            <div className="space-y-3">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="bg-muted rounded-xl p-3 border border-border animate-pulse">
                  <div className="h-4 bg-gray-300 rounded mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                </div>
              ))}
            </div>
          ) : expiringItems.length > 0 ? (
            <div className="space-y-3">
              {expiringItems.map((item) => (
                <ExpiringItemCard key={item.id} item={item} />
              ))}
            </div>
          ) : (
            <div className="text-center py-4 text-muted-foreground">
              <AlertTriangle size={24} className="mx-auto mb-2 opacity-50" />
              <p className="text-sm">No items expiring soon</p>
            </div>
          )}
        </div>

        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading font-semibold text-foreground">Recent Recipes</h2>
            <History size={16} className="text-muted-foreground" />
          </div>
          
          <div className="space-y-3">
            {recentRecipes.map((recipe) => (
              <motion.div
                key={recipe.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-muted rounded-xl p-3 border border-border"
              >
                <h3 className="font-medium text-sm text-foreground mb-1">{recipe.title}</h3>
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>{recipe.time} min</span>
                  <span>{recipe.calories} cal</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>


      </div>
    </motion.div>
  );
};

export default RecentActivity;